import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import {
  deriveStatus,
  lastAnsweredAt,
  useConnectionStore,
  type ConnectionStatus,
} from "@/lib/connection/connectionStore";

/** `busy` must last this long before the dot turns amber … */
export const BUSY_SHOW_DELAY_MS = 300;
/** … and once amber, stays amber at least this long. No flicker on quick writes. */
export const BUSY_MIN_VISIBLE_MS = 700;

const isBad = (s: ConnectionStatus) => s === "offline" || s === "unreachable";

/**
 * Flicker guard for the amber leg only. Red states show at once; leaving red
 * shows at once — also into amber, which skips its own show-delay then, so a
 * recovery never looks 300 ms slower than it was. Timers are cleared on every
 * status change and on unmount (StrictMode double-mounts in dev).
 */
export function useDisplayedStatus(status: ConnectionStatus): ConnectionStatus {
  const [displayed, setDisplayed] = useState<ConnectionStatus>(
    status === "busy" ? "live" : status,
  );
  const displayedRef = useRef(displayed);
  displayedRef.current = displayed;
  const busyShownAtRef = useRef<number | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    if (status === "busy") {
      if (isBad(displayedRef.current)) {
        busyShownAtRef.current = Date.now();
        setDisplayed("busy");
      } else {
        timer = window.setTimeout(() => {
          busyShownAtRef.current = Date.now();
          setDisplayed("busy");
        }, BUSY_SHOW_DELAY_MS);
      }
    } else {
      const shownAt = busyShownAtRef.current;
      const remaining =
        shownAt == null ? 0 : BUSY_MIN_VISIBLE_MS - (Date.now() - shownAt);
      if (isBad(status) || remaining <= 0) {
        busyShownAtRef.current = null;
        setDisplayed(status);
      } else {
        timer = window.setTimeout(() => {
          busyShownAtRef.current = null;
          setDisplayed(status);
        }, remaining);
      }
    }
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [status]);

  return displayed;
}

/** The statuses that paint a dot — `live` paints nothing (see `ConnectionDot`). */
type ShownStatus = Exclude<ConnectionStatus, "live">;

/**
 * Own tokens, not the `--color-font-*` trio: an 8px disc is non-text UI and
 * needs 3:1 against the bar (WCAG 1.4.11); the font colours are 2.8–3.1:1 on
 * the light bar because they were tuned for text next to an icon, not for a
 * dot that IS the signal. Spelled out per status — Tailwind v4 only emits
 * classes it finds literally in source. (`--color-connection-live` stays
 * declared: the check-in app pins the same hex and still paints green.)
 */
const DOT_CLASS: Record<ShownStatus, string> = {
  busy: "bg-[var(--color-connection-busy)]",
  offline: "bg-[var(--color-connection-offline)]",
  unreachable: "bg-[var(--color-connection-offline)]",
};

function formatTime(locale: string, epochMs: number): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(epochMs));
  } catch {
    return new Date(epochMs).toISOString().slice(11, 16);
  }
}

/**
 * What the live region last spoke, shared across mounts. SAppBar swaps the
 * whole bar for its error strip exactly when the connection dies, which
 * unmounts one dot and mounts another already-red one: without shared memory
 * that dot would either stay silent (a cold-load outage is never announced)
 * or repeat what the previous dot just said.
 */
let lastAnnouncedTone: "good" | "bad" | null = null;

/** Test seam — the module-level memory has no other reset path. */
export function __resetAnnouncementsForTests(): void {
  lastAnnouncedTone = null;
}

/**
 * The app-bar connection dot: amber = syncing, red = offline / server not
 * reachable — and NOTHING while live. A permanent green dot said nothing 99 %
 * of the time and read as "user online" or a notification state; red going
 * away is the recovery signal now (in the outage strip the whole strip gives
 * way with it). State comes from the connection store, which the axios
 * interceptors and `ConnectionWatcher` feed; the amber debounce still runs
 * while live, so a dot never appears for a write under 300 ms.
 *
 * Accessibility: the dot is `role="img"` with the full sentence as its name,
 * read on focus — NOT a live region, since a live region announces content
 * changes, not label changes. A separate visually-hidden `aria-live` span
 * carries text only when the dot ENTERS red and once when it recovers, so
 * amber blips are never spoken. That span is ALWAYS mounted, outside the
 * conditional dot: a live region only announces changes to a node that was
 * already there, so a span that unmounted with the dot would never speak
 * the recovery. It also carries `data-status`, the stable hook for tests —
 * "no dot in the DOM" alone is just as true before the bar has booted.
 * `sr-only` is absolutely positioned, so the span is no flex item and takes
 * no gap in the rows that mount it. Deliberately no `role="status"`: several
 * page-level specs query `getByRole("status")` for a single banner, and a
 * permanent second match would break them (Playwright strict mode).
 */
export function ConnectionDot({ className }: { className?: string }) {
  const t = useTranslations("appbar");
  const locale = useLocale();
  const rawStatus = useConnectionStore(deriveStatus);
  const status = useDisplayedStatus(rawStatus);

  const describe = (s: ConnectionStatus): string => {
    switch (s) {
      case "busy":
        return t("connectionBusy");
      case "offline":
        return t("connectionOffline");
      case "unreachable": {
        const at = lastAnsweredAt();
        return at == null
          ? t("connectionUnreachableNever")
          : t("connectionUnreachable", { time: formatTime(locale, at) });
      }
      default:
        return t("connectionLive");
    }
  };

  const label = describe(status);

  // Announce only the transitions that matter (into red, back out of red),
  // once per transition across remounts (see `lastAnnouncedTone`). Extra
  // effect runs (a new `t` identity, a re-formatted time in `label`) are
  // no-ops because the tone has not changed.
  const [announcement, setAnnouncement] = useState("");
  const liveLabel = t("connectionLive");
  useEffect(() => {
    const tone = isBad(status) ? "bad" : "good";
    if (tone === "bad" && lastAnnouncedTone !== "bad") {
      lastAnnouncedTone = "bad";
      setAnnouncement(label);
    } else if (tone === "good" && lastAnnouncedTone === "bad") {
      lastAnnouncedTone = "good";
      setAnnouncement(liveLabel);
    }
  }, [status, label, liveLabel]);

  return (
    <>
      {status !== "live" && (
        <HoverInfo
          message={label}
          className={`inline-flex items-center shrink-0 ${className ?? ""}`}
        >
          <span
            role="img"
            aria-label={label}
            tabIndex={0}
            data-testid="connection-dot"
            data-status={status}
            className="relative flex items-center justify-center w-6 h-6 rounded-full cursor-help focus-ring"
          >
            {/* The pulse is the only motion in the bar; Tailwind does not park
                `animate-*` under prefers-reduced-motion by itself. */}
            {status === "busy" && (
              <span className="absolute w-4 h-4 rounded-full bg-[var(--color-connection-busy)]/20 animate-pulse motion-reduce:animate-none" />
            )}
            <span className={`relative w-2 h-2 rounded-full ${DOT_CLASS[status]}`} />
          </span>
        </HoverInfo>
      )}
      <span
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid="connection-dot-announcement"
        data-status={status}
      >
        {announcement}
      </span>
    </>
  );
}
